import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { CREATE_PRICERANGE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { PostPriceRangePayloadRequest } from '../../../../../core/modules/master/interfaces/post-price-range';

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postPriceRange, getPriceRanges, priceRanges } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const schema = yup.object().shape({
    start_price: yup
      .number()
      .min(0, 'El precio mínimo es 0')
      .required('Campo requerido')
      .typeError('Formato incorrecto'),
    end_price: yup
      .number()
      .required('Campo requerido')
      .typeError('Formato incorrecto')
      .when('start_price', (end_price, schema) =>
        schema.moreThan(yup.ref('start_price'), 'El precio final debe ser mayor que el precio inicial')
      ),
    description: yup.string().required('Campo requerido'),
  });

  const { control, formState, handleSubmit, resetField } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Watch form values for real-time validation
  const watchedValues = useWatch({ control });
  
  // Check for duplicates in real-time
  const duplicateError = useMemo(() => {
    if (!watchedValues.start_price || !watchedValues.end_price) return null;
    
    const isDuplicate = priceRanges.some(range => 
      range.start_price === watchedValues.start_price && 
      range.end_price === watchedValues.end_price
    );
    
    return isDuplicate ? 'Ya existe un rango de precios con estos valores' : null;
  }, [watchedValues.start_price, watchedValues.end_price, priceRanges]);

  const resetForm = useCallback(() => {
    resetField('start_price');
    resetField('end_price');
    resetField('description');
    closeDialog(CREATE_PRICERANGE_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (values: PostPriceRangePayloadRequest) => {
      if (companyId) {
        // Validar duplicados antes de enviar
        const isDuplicate = priceRanges.some(range => 
          range.start_price === values.start_price && 
          range.end_price === values.end_price
        );
        
        if (isDuplicate) {
          Swal.fire({
            title: "Error",
            text: "Ya existe un rango de precios con estos valores",
            icon: "error",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Aceptar",
          });
          return;
        }
        
        setLoading(true);
        postPriceRange(companyId, values, () => {
          resetForm();
          getPriceRanges(companyId);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, postPriceRange, resetForm, getPriceRanges, priceRanges]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_PRICERANGE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Rango de Precios
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-24">
                    <Controller
                      name="start_price"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Precio Inicio"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.start_price}
                          helperText={errors?.start_price?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="end_price"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Precio Fin"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.end_price}
                          helperText={errors?.end_price?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {duplicateError && (
                    <div className="mb-24">
                      <Typography color="error" className="text-md text-center">
                        {duplicateError}
                      </Typography>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading || !!duplicateError}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
